import {TrendingDown, Calendar, Banknote, PiggyBank, AlertTriangle} from 'lucide-react'
import type { LoanResults } from "@/utils/calculator"
import { formatCurrency } from "@/utils/calculator"

interface ResultsDisplayProps {
  results: LoanResults
}

export default function ResultsDisplay({ results }: ResultsDisplayProps) {
  const cards = [
    {
      label: "Monthly Payment",
      value: formatCurrency(results.monthlyPayment),
      icon: Banknote,
      color: "from-emerald-600 to-emerald-700",
      textColor: "text-white",
      highlight: true,
    },
    {
      label: "Total Interest",
      value: formatCurrency(results.totalInterest),
      icon: TrendingDown,
      color: "from-amber-500 to-amber-600",
      textColor: "text-white",
    },
    {
      label: "Total Cost of Loan",
      value: formatCurrency(results.totalCost),
      icon: PiggyBank,
      color: "from-slate-600 to-slate-700",
      textColor: "text-white",
    },
    {
      label: "Payoff Date",
      value: results.payoffDate,
      icon: Calendar,
      color: "from-emerald-800 to-emerald-900",
      textColor: "text-white",
    },
  ]

  return (
    <div className="space-y-4">
      {/* Summary header */}
      <div className="bg-gradient-to-r from-emerald-900 to-emerald-800 rounded-2xl p-5 text-white">
        <h3
          className="text-lg font-bold mb-3"
          style={{ fontFamily: "'DM Serif Display', serif" }}
        >
          Your Land Loan Summary
        </h3>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
          <div className="flex justify-between">
            <span className="text-emerald-300">Land Price:</span>
            <span className="font-semibold text-white">{formatCurrency(results.landPrice)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-emerald-300">Down Payment:</span>
            <span className="font-semibold text-white">{formatCurrency(results.downPaymentAmount)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-emerald-300">Loan Amount:</span>
            <span className="font-semibold text-white">{formatCurrency(results.loanAmount)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-emerald-300">Interest Rate:</span>
            <span className="font-semibold text-white">{results.interestRate}%</span>
          </div>
          <div className="flex justify-between">
            <span className="text-emerald-300">Loan Term:</span>
            <span className="font-semibold text-white">{results.loanTermYears} years ({results.actualMonths} mo)</span>
          </div>
        </div>
      </div>

      {/* Result cards */}
      <div className="grid grid-cols-2 gap-3">
        {cards.map((card, index) => (
          <div
            key={card.label}
            className={`bg-gradient-to-br ${card.color} rounded-xl p-4 ${card.textColor} animate-count-up`}
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="flex items-center gap-2 mb-2 opacity-80">
              <card.icon className="w-4 h-4" />
              <span className="text-xs font-semibold uppercase tracking-wider">{card.label}</span>
            </div>
            <p
              className={`font-bold ${card.highlight ? "text-2xl" : "text-xl"} truncate`}
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {card.value}
            </p>
          </div>
        ))}
      </div>

      {/* Balloon Payment Warning */}
      {results.balloonPaymentAmount > 0 ? (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 flex items-start gap-3 animate-fade-in">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-amber-800 text-sm">Balloon Payment Due</p>
            <p className="text-amber-700 text-sm mt-0.5">
              A balloon payment of{" "}
              <strong>{formatCurrency(results.balloonPaymentAmount)}</strong> will be due at the
              end of your balloon term. Make sure you plan for refinancing or have funds available.
            </p>
          </div>
        </div>
      ) : null}
    </div>
  )
}
